import { UserButton } from "@clerk/clerk-react";
import { MemberRole } from "@growth/schema";
import { cn } from "@growth/ui";
import {
  Activity,
  BookOpenText,
  Bot,
  Building,
  Building2,
  CalendarDays,
  Compass,
  Contact,
  Crosshair,
  FileText,
  History,
  Inbox,
  type LucideIcon,
  Mail,
  Newspaper,
  PenLine,
  Search,
  Send,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  Telescope,
  UsersRound,
  WandSparkles,
} from "lucide-react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { AgentsDock } from "../components/agents-dock";
import { useActive, useSwitchActive } from "../lib/active";

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  match: (path: string) => boolean;
  minRole?: MemberRole;
};

const starts = (prefix: string) => (path: string) => path.startsWith(prefix);

const postNav: NavItem[] = [
  {
    to: "/today",
    label: "Today",
    icon: Inbox,
    match: (path) => path.startsWith("/today") || path.startsWith("/inbox"),
  },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, match: starts("/calendar") },
  {
    to: "/content",
    label: "Content",
    icon: PenLine,
    match: (path) => path.startsWith("/content") && !path.startsWith("/content-setup"),
  },
];

const leadsNav: NavItem[] = [
  { to: "/leads", label: "Leads", icon: Target, match: (path) => path === "/leads" },
  { to: "/leads/icp", label: "Ideal customer", icon: Crosshair, match: starts("/leads/icp") },
  { to: "/leads/accounts", label: "Accounts", icon: Building, match: starts("/leads/accounts") },
  { to: "/leads/people", label: "People", icon: Contact, match: starts("/leads/people") },
  { to: "/leads/outreach", label: "Outreach", icon: Send, match: starts("/leads/outreach") },
  { to: "/leads/sending", label: "Sending", icon: Mail, match: starts("/leads/sending") },
];

const understandNav: NavItem[] = [
  { to: "/setup", label: "Brand setup", icon: Compass, match: starts("/setup") },
  { to: "/knowledge", label: "Brand profile", icon: BookOpenText, match: starts("/knowledge") },
  { to: "/claims", label: "Claims", icon: ShieldCheck, match: starts("/claims") },
  { to: "/research", label: "Research", icon: Telescope, match: starts("/research") },
];

const blogNav: NavItem[] = [
  { to: "/seo", label: "SEO", icon: Search, match: starts("/seo") },
  {
    to: "/",
    label: "Blog posts",
    icon: FileText,
    match: (path) => path === "/" || path.startsWith("/posts"),
  },
  { to: "/ai", label: "Write with AI", icon: WandSparkles, match: starts("/ai") },
  {
    to: "/blog-settings",
    label: "Blog settings",
    icon: Newspaper,
    match: starts("/blog-settings"),
  },
];

const teamNav: NavItem[] = [
  { to: "/agents", label: "Agents", icon: Bot, match: starts("/agents") },
  {
    to: "/content-setup",
    label: "Content setup",
    icon: SlidersHorizontal,
    match: starts("/content-setup"),
  },
];

const workspaceNav: NavItem[] = [
  { to: "/authors", label: "Authors", icon: UsersRound, match: starts("/authors") },
  { to: "/members", label: "Members", icon: UsersRound, match: starts("/members") },
  { to: "/settings", label: "Settings", icon: Settings, match: starts("/settings") },
  {
    to: "/activity",
    label: "Activity",
    icon: History,
    match: starts("/activity"),
    minRole: "OWNER",
  },
];

const platformNav: NavItem[] = [
  { to: "/platform", label: "Workspaces", icon: Building2, match: starts("/platform") },
  { to: "/system", label: "System", icon: Activity, match: starts("/system") },
];

const NavGroup = ({ title, items }: { title: string; items: NavItem[] }) => {
  const { pathname } = useLocation();
  if (!items.length) return null;
  return (
    <div className="space-y-1">
      <p className="px-3 text-[10px] font-semibold uppercase tracking-widest text-muted">{title}</p>
      {items.map(({ to, label, icon: Icon, match }) => {
        const active = match(pathname);
        return (
          <Link
            key={to}
            to={to}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
              active ? "bg-paper font-medium" : "text-muted hover:bg-paper hover:text-ink",
            )}
          >
            <Icon className="size-4" />
            {label}
          </Link>
        );
      })}
    </div>
  );
};

const selectClass =
  "h-9 w-full rounded-md border border-line bg-white px-2 text-sm text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20";

/** Which business, and which of its brands. Switching drops everything cached for the old one. */
const Switchers = () => {
  const { me, workspace, brand } = useActive();
  const switchActive = useSwitchActive();
  if (!me || !workspace) return null;

  return (
    <div className="space-y-2">
      {me.workspaces.length > 1 ? (
        <label className="block space-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted">
            Workspace
          </span>
          <select
            className={selectClass}
            value={workspace.id}
            onChange={(event) => {
              const next = me.workspaces.find((candidate) => candidate.id === event.target.value);
              if (next) void switchActive(next.id, next.brands[0]?.id ?? null);
            }}
          >
            {me.workspaces.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted">
          {workspace.name}
        </p>
      )}
      {workspace.brands.length > 0 ? (
        <div role="radiogroup" aria-label="Brand" className="grid gap-1 rounded-lg bg-paper p-1">
          {workspace.brands.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              role="radio"
              aria-checked={brand?.id === candidate.id}
              onClick={() => void switchActive(workspace.id, candidate.id)}
              className={cn(
                "rounded-md px-2 py-1.5 text-left text-xs font-medium transition-colors",
                brand?.id === candidate.id
                  ? "bg-white text-ink shadow-sm"
                  : "text-muted hover:text-ink",
              )}
            >
              {candidate.name}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted">
          No brands yet.{" "}
          <Link to="/settings" className="text-accent underline">
            Add one
          </Link>
        </p>
      )}
    </div>
  );
};

export const AdminLayout = () => {
  const { me, workspace, brand, atLeast } = useActive();
  const allowed = (items: NavItem[]) =>
    items.filter((item) => !item.minRole || atLeast(item.minRole));

  return (
    <div className="flex min-h-screen bg-paper/40 text-ink">
      <aside className="sticky top-0 flex h-screen w-60 shrink-0 flex-col gap-5 overflow-y-auto border-r border-line bg-white p-4">
        <p className="font-serif text-xl">Growth Engine</p>
        <Switchers />
        <nav className="flex flex-1 flex-col gap-5">
          {workspace && brand && (
            <>
              <NavGroup title="Plan and post" items={allowed(postNav)} />
              <NavGroup title="Leads" items={allowed(leadsNav)} />
              <NavGroup title="Understand" items={allowed(understandNav)} />
              <NavGroup title="Blog" items={allowed(blogNav)} />
              <NavGroup title="Agents and setup" items={allowed(teamNav)} />
            </>
          )}
          {workspace && <NavGroup title="Workspace" items={allowed(workspaceNav)} />}
          {me?.user.isPlatformAdmin && <NavGroup title="Platform" items={platformNav} />}
        </nav>
        <div className="flex items-center gap-2">
          <UserButton showName afterSignOutUrl="/sign-in" />
          {workspace && (
            <span className="ml-auto rounded-full bg-paper px-2 py-0.5 text-[10px] font-medium text-muted">
              {workspace.role.toLowerCase()}
            </span>
          )}
        </div>
      </aside>
      <main className="min-w-0 flex-1 px-6 py-8 lg:px-10">
        <Outlet />
      </main>
      {workspace && <AgentsDock />}
    </div>
  );
};
